/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { listBodiesMessages } from 'src/luis/verticals/introSlides/components/ListBodiesMessages';

export type ListBodiesProps = {
    /**
     * The current selected Item.
     */
    currentSelection: number;
};

const slidesTheme = getScopedTheme('IntroSlides');
const { styleComponent } = getStylist('IntroSlides');

const Container = styleComponent<IStackProps>(Stack)('Container', theme => ({
    maxWidth: theme.listBodyMaxWidth,
    padding: theme.listBodyPadding
}));

const Header = styleComponent<ITextProps>(Text)(
    'Header',
    theme =>
        ({
            fontWeight: theme.fontWeight as Property.FontWeight,
            textAlign: theme.listBodiesTextAlign as Property.TextAlign
        } as Record<string, string>)
);

const Body = styleComponent(LuisText)(
    'Body',
    theme => ({ textAlign: theme.listBodiesTextAlign as Property.TextAlign } as Record<string, string>)
);

/**
 * A wrapper for the list body content.
 */
const ListBodiesContainer = (props: React.PropsWithChildren<object>) => (
    <Container horizontalAlign="start" tokens={{ childrenGap: slidesTheme.listBodiesChildrenGap }}>
        {props.children}
    </Container>
);

/**
 * The component rendering the side list with the selection highlighted.
 */
export const ListBodies = (props: ListBodiesProps) => {
    const { currentSelection } = props;
    const { localize } = useLocalization();
    const { isXXLargeOrGreater } = useResponsiveMode();

    switch (currentSelection) {
        case 1:
            return (
                <ListBodiesContainer>
                    <Header variant={isXXLargeOrGreater ? 'xLarge' : 'large'}>{localize(listBodiesMessages.titleOne)}</Header>

                    <Body>
                        {localize(listBodiesMessages.bodyTextOneA)}
                        &nbsp;
                        <b>{localize(listBodiesMessages.intent)}</b>
                        &nbsp;
                        {localize(listBodiesMessages.bodyTextOneB)}
                        &nbsp;
                        <b>{localize(listBodiesMessages.entities)}</b>
                        &nbsp;
                        {localize(listBodiesMessages.bodyTextOneC)}
                    </Body>
                </ListBodiesContainer>
            );
        case 2:
            return (
                <ListBodiesContainer>
                    <Header variant={isXXLargeOrGreater ? 'xLarge' : 'large'}>{localize(listBodiesMessages.titleTwo)}</Header>

                    <Body>{localize(listBodiesMessages.bodyTextTwoA)}</Body>

                    <Body>
                        {localize(listBodiesMessages.bodyTextTwoB)}
                        &nbsp;
                        <b>{localize(listBodiesMessages.dataImbalance)}</b>
                    </Body>
                </ListBodiesContainer>
            );
        case 3:
            return (
                <ListBodiesContainer>
                    <Header variant={isXXLargeOrGreater ? 'xLarge' : 'large'}>{localize(listBodiesMessages.titleThree)}</Header>

                    <Body>{localize(listBodiesMessages.bodyTextThreeA)}</Body>

                    <Body>
                        {localize(listBodiesMessages.bodyTextThreeB)}
                        &nbsp;
                        <b>{localize(listBodiesMessages.unclearPredictions)}</b>
                    </Body>
                </ListBodiesContainer>
            );
        case 4:
            return (
                <ListBodiesContainer>
                    <Header variant={isXXLargeOrGreater ? 'xLarge' : 'large'}>{localize(listBodiesMessages.titleFour)}</Header>

                    <Body>
                        {localize(listBodiesMessages.bodyTextFourA)}
                        &nbsp;
                        <b>{localize(listBodiesMessages.activeLearning)}</b>
                        &nbsp;
                        {localize(listBodiesMessages.bodyTextFourB)}
                    </Body>

                    <Body>{localize(listBodiesMessages.bodyTextFourC)}</Body>
                </ListBodiesContainer>
            );
        default:
            return (
                <ListBodiesContainer>
                    <Header variant={isXXLargeOrGreater ? 'xLarge' : 'large'}>{localize(listBodiesMessages.titleFive)}</Header>

                    <Body>
                        <b>{localize(listBodiesMessages.phraseLists)}</b>
                        &nbsp;
                        {localize(listBodiesMessages.bodyTextFiveA)}
                        <b>{localize(listBodiesMessages.patterns)}</b>
                        &nbsp;
                        {localize(listBodiesMessages.bodyTextFiveB)}
                    </Body>

                    <Body>{localize(listBodiesMessages.bodyTextFiveC)}</Body>
                </ListBodiesContainer>
            );
    }
};
